import { useFetchWrapper } from '_helpers';

export { useHomeAssignmentActions };

function useHomeAssignmentActions() {
    const fetchWrapper = useFetchWrapper();

    async function getAssignments(classId) {
        const res = await fetchWrapper.get(`/api/classes/${classId}/home-assignments`, null, null);
        if (!res) return [];
        const json = await res.json();
        // Endpoint returns the array directly.
        return Array.isArray(json) ? json : (json.message || []);
    }

    async function createAssignment(classId, { title, description, due_date, grace_hours }) {
        const body = {
            title,
            description: description || '',
        };
        if (due_date) body.due_date = due_date;
        if (grace_hours !== undefined && grace_hours !== null && grace_hours !== '') {
            body.grace_hours = grace_hours;
        }
        const res = await fetchWrapper.post(
            `/api/classes/${classId}/home-assignments`,
            'application/json',
            JSON.stringify(body)
        );
        return await res.json();
    }

    async function deleteAssignment(classId, assignmentId) {
        const res = await fetchWrapper.delete(
            `/api/classes/${classId}/home-assignments/${assignmentId}`,
            null, null
        );
        return await res.json();
    }

    async function getSubmissions(classId, assignmentId) {
        const res = await fetchWrapper.get(
            `/api/classes/${classId}/home-assignments/${assignmentId}/submissions`,
            null, null
        );
        if (!res) return [];
        const json = await res.json();
        return Array.isArray(json) ? json : (json.message || []);
    }

    async function generateLink(classId, assignmentId) {
        const res = await fetchWrapper.post(
            `/api/classes/${classId}/home-assignments/${assignmentId}/link`,
            'application/json',
            JSON.stringify({})
        );
        return await res.json();
    }

    // File upload must be multipart/form-data. We use a raw fetch (not useFetchWrapper)
    // so the browser sets the multipart boundary itself — passing FormData through the
    // wrapper risks an explicit Content-Type that breaks the boundary.
    async function submitFile(classId, assignmentId, file) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(
            `/api/classes/${classId}/home-assignments/${assignmentId}/submit`,
            { method: 'POST', credentials: 'include', body: formData }
        );
        let json = {};
        try { json = await res.json(); } catch (e) { /* non-JSON */ }
        return { ok: res.ok, ...json };
    }

    // Teacher reference attachment upload — multipart, same raw-fetch reasoning as submitFile.
    async function addAttachment(classId, assignmentId, file) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(
            `/api/classes/${classId}/home-assignments/${assignmentId}/attachment`,
            { method: 'POST', credentials: 'include', body: formData }
        );
        let json = {};
        try { json = await res.json(); } catch (e) { /* non-JSON */ }
        return { ok: res.ok, ...json };
    }

    // Teacher "red-pen" grading save — multipart (marked PNGs + grade/comment/meta).
    // Raw fetch so the browser sets the multipart boundary itself (see submitFile).
    // `files` is an array of Blobs/Files aligned with `meta` ([{source_index, page}]).
    // When `replaceFiles` is true the uploaded set replaces the stored marked copies;
    // when false (grade/comment-only) no files are sent and existing markup is kept.
    async function saveReview(classId, assignmentId, studentId, { grade, comment, files, meta, replaceFiles, annotations }) {
        const formData = new FormData();
        if (grade !== undefined && grade !== null && grade !== '') {
            formData.append('grade', String(grade));
        } else {
            formData.append('grade', '');
        }
        formData.append('comment', comment || '');
        if (replaceFiles) {
            formData.append('replace_files', 'true');
            formData.append('meta', JSON.stringify(meta || []));
            formData.append('annotations', annotations || ''); // raw shape JSON → re-editable markup
            (files || []).forEach((f, i) => {
                formData.append('files', f, (f && f.name) || `cham-${i + 1}.png`);
            });
        }
        const res = await fetch(
            `/api/classes/${classId}/home-assignments/${assignmentId}/submissions/${studentId}/review`,
            { method: 'POST', credentials: 'include', body: formData }
        );
        let json = {};
        try { json = await res.json(); } catch (e) { /* non-JSON */ }
        return { ok: res.ok, status: res.status, ...json };
    }

    // Teacher delete reference attachment — plain JSON DELETE via the wrapper.
    async function deleteAttachment(classId, assignmentId, fileId) {
        const res = await fetchWrapper.delete(
            `/api/classes/${classId}/home-assignments/${assignmentId}/attachment/${fileId}`,
            null, null
        );
        return await res.json();
    }

    return {
        getAssignments,
        createAssignment,
        deleteAssignment,
        getSubmissions,
        generateLink,
        submitFile,
        addAttachment,
        deleteAttachment,
        saveReview,
    };
}
